import React, { useContext, useEffect, useState } from 'react';
import { useLocation } from '@reach/router';
import { useMutation } from '@apollo/react-hooks';
import styles from '@components/account-consultation-result/account-consultation-result.module.scss';
import { useAccountText } from '@hooks/useContent';
import { useGetAccountData } from '@features/account/hooks/useGetAccountData';
import { ResultPageContext } from '@features/account/pages/result/store/store';
import { GlobalContext } from '@store/global-state';
import PillLoader from './PillLoader';
import { navigate } from 'gatsby';
import { RESULT_STEP_URLS } from '../../constants/constants';
import { PROMOTE_GUEST_CONSULTATION } from '@queries/user';
import { GRAPH_QL_ERROR_TYPES } from '@constants/enums';
import { ERROR_MESSAGES } from '@src/constants';

const PillLoading = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const guestId = params.get('promoteGuestConsultation');
  const variant = params.get('consultationVariant');
  const needsPromotion = !!(guestId && variant);

  useGetAccountData();

  const {
    dataGoals,
    dataHealthResults,
    loadingGoals,
    loadingRecommended,
    dataRecommendedProducts,
  } = useContext(ResultPageContext);

  const { setErrorModalMessage } = useContext(GlobalContext);
  const { directusAccountConsultationResult } = useAccountText();

  const [animationCompleted, setAnimationCompleted] = useState(false);
  const [promotionComplete, setPromotionComplete] = useState(!needsPromotion);

  const [promoteGuest] = useMutation(PROMOTE_GUEST_CONSULTATION, {
    variables: { guestId: guestId || '', variant: variant || '' },
    refetchQueries: ['health_goals', 'recommended_products', 'health_results'],
    awaitRefetchQueries: true,
    onCompleted: () => {
      setPromotionComplete(true);
    },
    onError: error => {
      setPromotionComplete(true);
      const invalidGuestId = error?.graphQLErrors?.find(
        (e: any) => e.errorType === GRAPH_QL_ERROR_TYPES.InvalidGuestId
      );
      const hpFailed = error?.graphQLErrors?.find(
        (e: any) => e.errorType === GRAPH_QL_ERROR_TYPES.HealthProfileGenerationFailed
      );
      if (invalidGuestId) {
        setErrorModalMessage(ERROR_MESSAGES.invalidGuestIdForPromotion);
      } else if (hpFailed) {
        setErrorModalMessage(ERROR_MESSAGES.healthProfileGenerationFailed);
      }
    },
  });

  useEffect(() => {
    if (needsPromotion) {
      promoteGuest();
    }
  }, []);

  useEffect(() => {
    if (
      animationCompleted &&
      promotionComplete &&
      !loadingGoals &&
      !loadingRecommended &&
      dataHealthResults &&
      dataGoals
    ) {
      if (
        dataRecommendedProducts &&
        dataRecommendedProducts?.health_recommendedProducts?.length < 1
      ) {
        navigate('/account/recommendation');
        return;
      }
      navigate(RESULT_STEP_URLS.step_1);
    }
  }, [animationCompleted, promotionComplete, loadingGoals, loadingRecommended, dataHealthResults, dataGoals, dataRecommendedProducts]);

  return (
    <PillLoader setAnimationCompleted={setAnimationCompleted}>
      <div className={styles.textLoader}>
        <span className={styles.message}>
          {directusAccountConsultationResult.supplement_list_text_1}
        </span>
        <span className={styles.message}>
          {directusAccountConsultationResult.supplement_list_text_2}
        </span>
        <span className={styles.message}>
          {directusAccountConsultationResult.supplement_list_text_3}
        </span>
        <span className={styles.message}>
          {directusAccountConsultationResult.supplement_list_text_4}
        </span>
        <span className={styles.message}>
          {directusAccountConsultationResult.supplement_list_text_5}
        </span>
      </div>
    </PillLoader>
  );
};

export default PillLoading;
